import { sharedCoverLetterClientDocumentVariable } from '@xing-com/crate-lebenslauf-shared-client-document-variable';

import type {
  CoverLetterActions,
  DocumentActions,
  FileAttachmentActions,
  ThemeActions,
  ValidationActions,
} from './types';
import { useVitaFields } from './use-vita-fields';
import {
  updateLocalStorage,
  addEducationalExperienceFields,
  addProfessionalDescriptionField,
  addProfessionalInformationFields,
  addProfileImage,
  addSkill,
  deleteEducationalExperienceFields,
  deleteProfessionalExperienceFields,
  deleteProfessionalInformationFields,
  deleteProfileImage,
  deleteSkill,
  editEducationalExperienceFields,
  editPersonalInformation,
  editProfessionalInformationFields,
} from './utils';
import { sharedClientDocumentVariableWithLocalStorage } from './utils/sharedClientDocumentVariableWithLocalStorage';

type UseClientDocumentActions = () => {
  coverLetterActions: CoverLetterActions;
  coverLetterFileAttachmentActions: FileAttachmentActions;
  coverLetterThemeActions: ThemeActions;
  coverLetterDocumentActions: DocumentActions;
  coverLetterValidationActions: ValidationActions;
};

export const useCoverLetterActions: UseClientDocumentActions = () => {
  const vitaFieldsData = useVitaFields();

  const coverLetterActions: CoverLetterActions = ({ type, value }) => {
    const doc = sharedCoverLetterClientDocumentVariable();

    switch (type) {
      case 'edit-cover-letter': {
        sharedClientDocumentVariableWithLocalStorage({
          ...doc,
          ...value,
        });
        break;
      }
      case 'edit-personal-information-fields': {
        editPersonalInformation(value);

        break;
      }

      case 'add-professional-information-fields': {
        addProfessionalInformationFields(doc);

        break;
      }

      case 'edit-professional-information-fields': {
        editProfessionalInformationFields(doc, value, vitaFieldsData);
        break;
      }

      case 'delete-professional-information-fields': {
        deleteProfessionalInformationFields(doc, value);

        break;
      }

      case 'add-professional-description-field': {
        addProfessionalDescriptionField(doc, value);

        break;
      }

      case 'delete-professional-description-field': {
        deleteProfessionalExperienceFields(doc, value);

        break;
      }

      case 'add-educational-experience-fields': {
        addEducationalExperienceFields(doc);

        break;
      }

      case 'edit-educational-experience-fields': {
        editEducationalExperienceFields(doc, value);

        break;
      }

      case 'delete-educational-experience-fields': {
        deleteEducationalExperienceFields(doc, value);

        break;
      }

      case 'add-skill': {
        addSkill(doc, value);

        break;
      }

      case 'delete-skill': {
        deleteSkill(doc, value);

        break;
      }

      case 'edit-job-description': {
        updateLocalStorage(`jobDetails.description`, value);

        break;
      }

      case 'edit-job-title': {
        updateLocalStorage(`jobDetails.title`, value);

        break;
      }

      default:
        break;
    }
  };

  const coverLetterFileAttachmentActions: FileAttachmentActions = ({
    type,
    value,
  }) => {
    const doc = sharedCoverLetterClientDocumentVariable();

    switch (type) {
      case 'add-profile-picture':
        addProfileImage(value);

        break;

      case 'delete-profile-picture':
        deleteProfileImage(doc);
    }
  };

  const coverLetterThemeActions: ThemeActions = ({ type, value }) => {
    switch (type) {
      case 'change-color': {
        updateLocalStorage(`theme.color`, value);

        break;
      }

      case 'change-template': {
        updateLocalStorage(`theme.template`, value);

        break;
      }

      default:
        break;
    }
  };

  const coverLetterDocumentActions: DocumentActions = ({ type, value }) => {
    const doc = sharedCoverLetterClientDocumentVariable();

    switch (type) {
      case 'edit-language':
        updateLocalStorage(`language`, value);

        break;

      case 'edit-content':
        updateLocalStorage(`content`, value);

        break;

      case 'edit-token': {
        updateLocalStorage('token', value);

        break;
      }

      case 'edit-profile-image-id': {
        updateLocalStorage('file-attachments', {
          fileAttachments: {
            ...doc.fileAttachments,
            profileImage: { ...doc.fileAttachments?.profileImage, id: value },
          },
        });

        break;
      }
    }
  };

  const coverLetterValidationActions: ValidationActions = ({ type, value }) => {
    const doc = sharedCoverLetterClientDocumentVariable();

    switch (type) {
      case 'add-invalid-form-input-name': {
        if (!doc.validation) return;

        const invalidFormInputNames = [...doc.validation.invalidFormInputNames];
        const updatedInvalidFormInputNames = [...invalidFormInputNames, value];

        updateLocalStorage(
          'validation.invalidFormInputNames',
          updatedInvalidFormInputNames
        );
        break;
      }

      case 'add-all-invalid-form-input-names': {
        updateLocalStorage('validation.invalidFormInputNames', value);

        break;
      }

      case 'delete-invalid-form-input-name': {
        if (!doc.validation) return;

        const invalidFormInputNames = [...doc.validation.invalidFormInputNames];
        const updatedInvalidFormInputNames = invalidFormInputNames.filter(
          (name) => name !== value
        );

        updateLocalStorage(
          'validation.invalidFormInputNames',
          updatedInvalidFormInputNames
        );

        break;
      }
    }
  };

  return {
    coverLetterActions,
    coverLetterFileAttachmentActions,
    coverLetterThemeActions,
    coverLetterDocumentActions,
    coverLetterValidationActions,
  };
};
